// React
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import { useActiveAuthProvider, useNotification } from '@refinedev/core';

// Mantine
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Group,
  PasswordInput,
  PinInput,
  ThemeIcon,
  Title,
  useComputedColorScheme,
  Text,
  Alert,
  Stack,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import {
  IconCheck,
  IconX,
  IconFingerprint,
  IconEyeOff,
  IconEyeCheck,
  IconLock
} from '@tabler/icons-react';

// Components
import { QDocLogo } from 'components/common/QDocLogo';

// Utils
import { getLoginCardBackground } from 'utils/styleUtils';

type Step = 'PASSWORD' | 'CODE';

/**
 * The updatePassword type is the page used to update the password of the user.
 * @see {@link https://refine.dev/docs/api-reference/mantine/components/mantine-auth-page/#update-password} for more details.
 */
export const UpdatePassword: React.FC = () => {
  const title = 'Update Password';
  useDocumentTitle(`${title} | QBilling`);
  const computedColorScheme = useComputedColorScheme('dark');
  const backgroundImage = `url(/background-${computedColorScheme}.svg)`;
  const navigate = useNavigate();
  const { open } = useNotification();
  const authProvider = useActiveAuthProvider();
  const [visible, { toggle }] = useDisclosure(false);

  const [step, setStep] = useState<Step>('PASSWORD');

  const VisibilityToggleIcon = ({ reveal }: { reveal: boolean }) =>
    reveal ? (
      <IconEyeOff
        color="gray"
        style={{
          width: 'var(--psi-icon-size)',
          height: 'var(--psi-icon-size)',
        }}
      />
    ) : (
      <IconEyeCheck
        color="gray"
        style={{
          width: 'var(--psi-icon-size)',
          height: 'var(--psi-icon-size)',
        }}
      />
    );

  /**
   * Password Requirements:
   * Minimum 8 characters
   * one uppercase letter,
   * one lowercase letter,
   * one number,
   * one special character,
   **/
  const passwordRegex =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;

  const form = useForm({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
      code: '',
    },
    validate: {
      newPassword: (value) =>
        passwordRegex.test(value)
          ? null
          : 'Password does not meet all the requirements.',
      confirmPassword: (value, values) =>
        value === values.newPassword ? null : 'Passwords do not match.',
      code: (value) =>
        !/^\S+$/.test(value) ? 'Invalid verification code.' : null,
    },
  });

  const { newPassword } = form.values;

  const requirements = useMemo(() => {
    return [
      {
        label: 'At least 8 characters',
        test: newPassword.length >= 8,
      },
      {
        label: 'One uppercase letter',
        test: /[A-Z]/.test(newPassword),
      },
      {
        label: 'One lowercase letter',
        test: /[a-z]/.test(newPassword),
      },
      {
        label: 'One digit',
        test: /\d/.test(newPassword),
      },
      {
        label: 'One special character',
        test: /[^A-Za-z0-9]/.test(newPassword),
      },
    ];
  }, [newPassword]);

  const handleUpdatePassword = async (values: any) => {
    const { newPassword, confirmPassword, code } = values;
    const email = localStorage.getItem('email') || '';
    const updateValues = { newPassword, confirmPassword, code, email };
    const response = (await authProvider.updatePassword(updateValues)) as {
      type: string;
      success: boolean;
    };
    if (response.type === 'onSuccess') {
      navigate('/');
    } else if (response.type === 'LimitExceededException') {
      open?.({
        message:
          'You have exceeded the maximum number of attempts. Please try again later.',
        type: 'error',
      });
    }
  };

  const handleNext = () => {
    if (
      !form.validateField('newPassword').hasError &&
      !form.validateField('confirmPassword').hasError
    ) {
      setStep('CODE');
    }
  };

  const renderPasswordForm = () => (
    <>
      <Alert
        title="Password Requirements"
        w="100%"
        mb="md"
        data-testid="password-requirements-alert"
      >
        {requirements.map((req, idx) => (
          <Flex
            key={idx}
            align="center"
            justify="space-between"
            mb="xs"
            data-testid={`password-requirement-${idx}`}
          >
            <Text
              size="sm"
              color={computedColorScheme === 'dark' ? 'white' : 'black'}
            >
              {req.label}
            </Text>
            <ThemeIcon
              size="sm"
              radius="xl"
              data-testid={`password-requirement-icon-${idx}`}
              style={{ backgroundColor: req.test ? 'green' : 'red' }}
            >
              {req.test ? <IconCheck size={14} /> : <IconX size={14} />}
            </ThemeIcon>
          </Flex>
        ))}
      </Alert>

      <Text size="sm" fw={500} mt="xs">
        <Group gap={6}>
          <IconLock size={16} /> New Password
        </Group>
      </Text>
      <PasswordInput
        placeholder="New Password"
        size="md"
        radius="lg"
        visible={visible}
        onVisibilityChange={toggle}
        leftSection={<IconLock size={16} />}
        visibilityToggleIcon={VisibilityToggleIcon}
        required
        data-testid="new-password-input"
        {...form.getInputProps('newPassword')}
      />
      <Text size="sm" fw={500} mt="xs">
        <Group gap={6}>
          <IconLock size={16} /> Confirm Password
        </Group>
      </Text>
      <PasswordInput
        placeholder="Confirm Password"
        size="md"
        radius="lg"
        visible={visible}
        onVisibilityChange={toggle}
        leftSection={<IconLock size={16} />}
        visibilityToggleIcon={VisibilityToggleIcon}
        required
        data-testid="confirm-password-input"
        {...form.getInputProps('confirmPassword')}
      />

      <Button
        mt="lg"
        fullWidth
        size="md"
        radius="xl"
        data-testid="next-button"
        onClick={() => handleNext()}
        // Avoid calling form.validate() during render to prevent infinite re-renders.
        // Disable if either password field is empty or existing errors are present.
        disabled={
          !form.values.newPassword ||
          !form.values.confirmPassword ||
          !!form.errors.newPassword ||
          !!form.errors.confirmPassword
        }
        style={{
          background: 'linear-gradient(to right, #22d3ee, #34d399)',
          boxShadow: '0 4px 25px rgba(52,211,153,0.25)',
        }}
        c={computedColorScheme === 'dark' ? 'white' : 'black'}
      >
        Next
      </Button>
    </>
  );

  const renderCodeForm = () => (
    <Flex
      direction="column"
      gap="md"
      align={'center'}
      justify={'center'}
      miw={'50%'}
      data-testid="code-form"
    >
      <Text
        size="sm"
        c={computedColorScheme === 'dark' ? 'white' : 'black'}
        mt="md"
      >
        Verification Code
      </Text>
      <PinInput
        length={6}
        type="number"
        radius="lg"
        placeholder="○"
        data-testid="verification-code-input"
        {...form.getInputProps('code')}
      />
      <Button
        mt="lg"
        fullWidth
        size="md"
        radius="xl"
        data-testid="confirm-button"
        onClick={() => handleUpdatePassword(form?.getValues())}
        // Avoid calling validateField during render; disable if code empty or existing error present.
        disabled={!form.values.code || !!form.errors.code}
        style={{
          background: 'linear-gradient(to right, #22d3ee, #34d399)',
          boxShadow: '0 4px 25px rgba(52,211,153,0.25)',
        }}
        c={computedColorScheme === 'dark' ? 'white' : 'black'}
      >
        Confirm
      </Button>
    </Flex>
  );

  return (
    <Box
      style={{
        backgroundImage: backgroundImage,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
        backgroundSize: 'cover',
        backgroundColor: 'transparent',
      }}
      data-testid="update-password-root"
    >
      <Flex
        direction="column"
        align="center"
        justify="center"
        style={{
          height: '100%',
        }}
      >
        <Card
          withBorder
          radius="xl"
          p="lg"
          style={{
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            background: getLoginCardBackground(computedColorScheme),
            borderColor: 'rgba(255,255,255,0.18)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
            transition: 'transform 160ms ease',
          }}
        >
          <Group align="center" gap="md" mt="lg" mb="xl">
            <QDocLogo />
            <div>
              <Title order={3}>{title}</Title>
              <Text size="sm" c="dimmed">
                {step === 'PASSWORD'
                  ? 'Create a new password'
                  : 'Enter the verification code sent to your email'}
              </Text>
            </div>
          </Group>

          <form
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            onSubmit={(e) => e.preventDefault()}
            data-testid="update-password-form"
          >
            <Stack gap="sm">
              {step === 'PASSWORD' && renderPasswordForm()}
              {step === 'CODE' && renderCodeForm()}
            </Stack>
          </form>

          <Divider label="Security" labelPosition="center" my="xs" />

          <Group justify="space-between" mt="xs">
            <Group gap={6}>
              <IconFingerprint size={16} />
              <Text size="xs" c="dimmed">
                Enterprise-grade security
              </Text>
            </Group>
            <Group gap="xs">
              <Anchor size="xs" href="https://qdoc.ca/terms-and-conditions/">
                Privacy
              </Anchor>
              <Anchor size="xs" href="https://qdoc.ca/terms-and-conditions/">
                Terms
              </Anchor>
              <Anchor size="xs" href="https://qdoc.ca/contact-us/">
                Help
              </Anchor>
            </Group>
          </Group>
        </Card>

        <Text ta="center" mt="xl" size="xs" c="dimmed">
          © {new Date().getFullYear()} QDoc Inc. All rights reserved.
        </Text>
      </Flex>
    </Box>
  );
};
